// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import type { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { Config } from "../config";
import {
    AssociateTemplateQueryArgs,
    AssociateTemplateResponse,
    CurrentParticipants,
    CurrentParticipantsQueryArgs,
    CurrentTemplatesQueryArgs,
    CurrentTemplatesResponse,
    CustomProperty,
    CustomPropertyModel,
    DownloadZipArchiveQueryArgs,
    GenerateDocumentsQueryArgs,
    GenerateDocumentsResponse,
    ParticipantsPayload,
    ParticipantsQueryArgs,
    SaveParticipantEditsQueryArgs,
    TemplateFiles,
    TemplateFilesQueryArgs,
} from "./types/generateDocumentsApiTypes";

const apiManagementRootUrl: string | undefined = Config.REACT_APP_CCL_API_MANAGEMENT_URL;

// Strangler switch for the Generate Documents backend (design decision 6):
// every request is redirected from the legacy BFF proxy (ccl-access2/generatedocuments/*,
// which forwards to ClassroomAdmin) to the new documents service
// (ccl-access2-documents/generatedocuments/*) — same routes, same wire shapes,
// so only the prefix changes.
const LEGACY_PREFIX = "ccl-access2/generatedocuments/";
const DOCUMENTS_API_PREFIX = "ccl-access2-documents/generatedocuments/";

const rawBaseQuery = fetchBaseQuery({
    baseUrl: apiManagementRootUrl,
    prepareHeaders: (headers, { getState }) => {
        //we should probably change this.
        const token = localStorage.getItem("token") as string;
        headers.set("authorization", `Bearer ${token}`);
        return headers;
    },
});

const flaggedBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = (
    args,
    api,
    extraOptions,
) => {
    // Local dev override (REACT_APP_DOCUMENTS_API_LOCAL_URL in public/config.js): route the
    // generate-documents calls to a documents-api of your choosing as absolute URLs, which
    // fetchBaseQuery uses as-is. Dev-server builds only — these requests carry the bearer
    // token, so a production build ignores the variable entirely no matter what the deployed
    // web.properties contains.
    const localDocumentsApiUrl: string | undefined =
        process.env.NODE_ENV !== "production" ? Config.REACT_APP_DOCUMENTS_API_LOCAL_URL : undefined;

    const targetPrefix = localDocumentsApiUrl
        ? `${localDocumentsApiUrl.endsWith("/") ? localDocumentsApiUrl : `${localDocumentsApiUrl}/`}generatedocuments/`
        : DOCUMENTS_API_PREFIX;

    if (typeof args === "string") {
        args = args.replace(LEGACY_PREFIX, targetPrefix);
    } else {
        args = { ...args, url: args.url.replace(LEGACY_PREFIX, targetPrefix) };
    }

    return rawBaseQuery(args, api, extraOptions);
};

// Define a service using a base URL and expected endpoints
export const cclTokenedGenerateDocumentsApi = createApi({
    reducerPath: "cclTokenedGenerateDocumentsApi",
    tagTypes: ["currentTemplates", "participants", "customProperties", "manageTemplates"],
    baseQuery: flaggedBaseQuery,
    endpoints: (builder) => ({
        participantsPayload: builder.query<ParticipantsPayload, ParticipantsQueryArgs>({
            query: (args) =>
                `ccl-access2/generatedocuments/participants?esKey=${args.esKey}&emailAddress=${args.emailAddress}`,
        }),
        templateFiles: builder.query<TemplateFiles[], TemplateFilesQueryArgs>({
            query: (args) =>
                `ccl-access2/generatedocuments/templatefiles?esKey=${args.esKey}&parent=${args.parent}`,
            providesTags: ["manageTemplates"],
        }),
        currentParticipants: builder.query<CurrentParticipants, CurrentParticipantsQueryArgs>({
            query: (args) => `ccl-access2/generatedocuments/currentparticipants?esKey=${args.id}`,
            providesTags: ["participants"],
        }),
        generateDocuments: builder.mutation<GenerateDocumentsResponse, GenerateDocumentsQueryArgs>({
            query: (args) => ({
                url: `ccl-access2/generatedocuments/generatedocuments/${args.esKey}`,
                method: "POST",
                body: args.body,
            }),
        }),
        associateTemplate: builder.mutation<AssociateTemplateResponse, AssociateTemplateQueryArgs>({
            query: (args) => ({
                url: `ccl-access2/generatedocuments/associatetemplate/${args.esKey}`,
                method: "POST",
                body: args.body,
            }),
            invalidatesTags: ["currentTemplates"],
        }),
        currentTemplates: builder.query<CurrentTemplatesResponse, CurrentTemplatesQueryArgs>({
            query: (args) => `ccl-access2/generatedocuments/currenttemplates/${args.esKey}`,
            providesTags: ["currentTemplates"],
        }),
        dynamicParticipants: builder.query<any, ParticipantsQueryArgs>({
            query: (args) =>
                `ccl-access2/generatedocuments/dynamicparticipants?esKey=${args.esKey}&emailAddress=${args.emailAddress}`,
            providesTags: ["participants"],
        }),
        saveParticipantEdits: builder.mutation<any, SaveParticipantEditsQueryArgs>({
            query: (args) => ({
                url: `ccl-access2/generatedocuments/saveparticipantdata/${args.esKey}`,
                method: "POST",
                body: args.body,
            }),
            invalidatesTags: ["participants"],
        }),
        customProperties: builder.query<CustomProperty[], number>({
            query: (args) => `ccl-access2/generatedocuments/customProperties/${args}`,
            providesTags: ["customProperties"],
        }),
        saveCustomProperty: builder.mutation<any, CustomPropertyModel>({
            query: (args) => ({
                url: `ccl-access2/generatedocuments/savecustomproperty/${args.programId}`,
                method: "POST",
                body: args,
            }),
            invalidatesTags: ["customProperties", "participants"],
        }),
        templatesChanged: builder.mutation<any, string>({
            query: (args) => ({
                url: `ccl-access2/generatedocuments/dynamicparticipants?esKey=${args}&emailAddress=r`,
                method: "GET",
            }),
            invalidatesTags: ["manageTemplates"],
        }),
        downloadZipArchive: builder.mutation<any, DownloadZipArchiveQueryArgs>({
            queryFn: async ({ path, programCode }, api, extraOptions, baseQuery) => {
                const result = await baseQuery({
                    url: `ccl-access2/generatedocuments/download?path=${path}&programCode=${programCode}`,
                    method: "POST",
                    responseHandler: (response) => response.blob(),
                });
                var blobresult = result?.data != null ? (result.data as Blob) : null;
                if (blobresult == null) return { data: null };

                var hiddenElement = document.createElement("a");
                var url = window.URL || window.webkitURL;
                var blobfile = url.createObjectURL(blobresult);
                hiddenElement.href = blobfile;
                hiddenElement.target = "_blank";
                hiddenElement.download = programCode;
                hiddenElement.click();
                return { data: null };
            },
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useParticipantsPayloadQuery,
    useTemplateFilesQuery,
    useCurrentParticipantsQuery,
    useGenerateDocumentsMutation,
    useAssociateTemplateMutation,
    useSaveParticipantEditsMutation,
    useCurrentTemplatesQuery,
    useDynamicParticipantsQuery,
    useCustomPropertiesQuery,
    useSaveCustomPropertyMutation,
    useDownloadZipArchiveMutation,
    useTemplatesChangedMutation,
} = cclTokenedGenerateDocumentsApi;
